import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import tokenStatsService, {
  TokenStats
} from '@/services/blockchain/token-stats.service'

interface AssetStatsProps {
  symbol: string
  network?: string
  address?: string
  priceChange24h: number
  className?: string
}

const DASH = '-'

const formatCompactUSD = (n: number | null) =>
  !n || n <= 0
    ? DASH
    : `$${new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 2
      }).format(n)}`

const formatCompactNumber = (n: number | null) =>
  n === null
    ? DASH
    : new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 2
      }).format(n)

const formatPercent = (n: number | null) =>
  n === null ? DASH : `${n.toFixed(2)}%`

const StatItem = ({
  label,
  value,
  valueClassName
}: {
  label: string
  value: string
  valueClassName?: string
}) => (
  <div className="flex flex-col gap-1">
    <p className="text-xs text-text-secondary">{label}</p>
    <p className={`text-sm font-bold text-text-main ${valueClassName || ''}`}>
      {value}
    </p>
  </div>
)

const AssetStats = ({
  symbol,
  network,
  address,
  priceChange24h,
  className
}: AssetStatsProps) => {
  const { t, i18n } = useTranslation()
  const [stats, setStats] = useState<TokenStats | null>(null)

  useEffect(() => {
    let cancelled = false
    setStats(null)

    if (!network) return

    tokenStatsService.getTokenStats(network, symbol, address).then((result) => {
      if (!cancelled) setStats(result)
    })

    return () => {
      cancelled = true
    }
  }, [network, symbol, address])

  const createdLabel = stats?.createdAt
    ? new Intl.DateTimeFormat(i18n.language, {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).format(new Date(stats.createdAt))
    : DASH

  const periods = [
    { label: '5m', value: stats?.performance.m5 ?? null },
    { label: '1h', value: stats?.performance.h1 ?? null },
    { label: '4h', value: stats?.performance.h4 ?? null },
    { label: '24h', value: priceChange24h }
  ]

  const hasTradeStats = stats?.transactions !== null && stats?.transactions !== undefined

  return (
    <div className={`flex flex-col gap-6 ${className || ''}`}>
      <div className="flex flex-col gap-5 bg-card border border-border-base rounded-3xl p-6 shadow-md">
        <h2 className="font-bold text-lg text-text-main">
          {t('assetDetails.stats.title')}
        </h2>
        <div className="grid grid-cols-2 gap-x-4 gap-y-5">
          <StatItem
            label={t('assetDetails.stats.market_cap')}
            value={formatCompactUSD(stats?.marketCap ?? null)}
          />
          <StatItem
            label={t('assetDetails.stats.volume_24h')}
            value={formatCompactUSD(stats?.volume24h ?? null)}
          />
          <StatItem
            label={t('assetDetails.stats.holders')}
            value={formatCompactNumber(stats?.holders ?? null)}
          />
          <StatItem label={t('assetDetails.stats.created')} value={createdLabel} />
          <StatItem
            label={t('assetDetails.stats.supply_percent')}
            value={
              stats?.supplyAmount != null
                ? `${formatCompactNumber(stats.supplyAmount)} (100.00%)`
                : DASH
            }
          />
          <StatItem
            label={t('assetDetails.stats.security_risk')}
            value={
              stats?.securityRisk === 'none'
                ? t('assetDetails.stats.security_risk_none')
                : stats?.securityRisk === 'risk'
                  ? t('assetDetails.stats.security_risk_found')
                  : DASH
            }
            valueClassName={
              stats?.securityRisk === 'none'
                ? 'text-success'
                : stats?.securityRisk === 'risk'
                  ? 'text-accent'
                  : ''
            }
          />
          <StatItem
            label={t('assetDetails.stats.liquidity')}
            value={formatCompactUSD(stats?.liquidityUSD ?? null)}
          />
          <StatItem
            label={t('assetDetails.stats.top_holders_percent')}
            value={formatPercent(stats?.topHoldersPercent ?? null)}
          />
        </div>
      </div>

      <div className="flex flex-col gap-5 bg-card border border-border-base rounded-3xl p-6 shadow-md">
        <h2 className="font-bold text-lg text-text-main">
          {t('assetDetails.performance.title')}
        </h2>
        <div className="grid grid-cols-4 gap-2">
          {periods.map((period) => (
            <div
              key={period.label}
              className="flex flex-col items-center gap-1 bg-text-main/5 rounded-xl py-3"
            >
              <p className="text-xs text-text-secondary">{period.label}</p>
              <p
                className={`text-sm font-bold ${
                  period.value === null
                    ? 'text-text-secondary'
                    : period.value >= 0
                      ? 'text-success'
                      : 'text-accent'
                }`}
              >
                {period.value === null
                  ? DASH
                  : `${period.value >= 0 ? '+' : ''}${period.value.toFixed(2)}%`}
              </p>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between text-xs text-text-secondary">
            <span>
              {t('assetDetails.performance.total_volume')}{' '}
              {formatCompactUSD(stats?.totalVolume ?? null)}
            </span>
            <span>
              {t('assetDetails.performance.transactions')}{' '}
              {formatCompactNumber(stats?.transactions ?? null)}
            </span>
          </div>
          {hasTradeStats && (
            <>
              <div className="flex w-full h-2 rounded-full overflow-hidden bg-text-main/10">
                <div
                  className="bg-success h-full"
                  style={{ width: `${stats?.buyPercent ?? 0}%` }}
                />
                <div
                  className="bg-accent h-full"
                  style={{ width: `${stats?.sellPercent ?? 0}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="text-success">
                  {stats?.buyPercent}% {t('assetDetails.performance.buy')}
                </span>
                <span className="text-accent">
                  {stats?.sellPercent}% {t('assetDetails.performance.sell')}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AssetStats
